import { ConditionalWrapperCriteria } from './types';

/**
 * Checks if a context object matches the given criteria.
 *
 * @template ContextType - The type of the context object
 * @param context - The context object to match against
 * @param criteria - The criteria to match. Each property can be a value, array of values, or undefined
 * @returns True if all criteria properties match their corresponding context values
 *
 * Matching rules:
 * - undefined criteria values always match
 * - If context value is an array and criteria is an array: matches if any criteria value exists in context
 * - If context value is an array and criteria is a single value: matches if criteria value exists in context
 * - If context value is not an array and criteria is an array: matches if context value exists in criteria
 * - If both are single values: matches if they are strictly equal
 */
function matchesCriteria<ContextType extends object>(
  context: ContextType,
  criteria: ConditionalWrapperCriteria<ContextType>,
) {
  return Object.entries(criteria).every(([key, value]) => {
    if (value === undefined) return true;

    const ctxValue =
      key in context ? context[key as keyof ContextType] : undefined;

    if (Array.isArray(ctxValue)) {
      if (Array.isArray(value)) {
        return value.some((x) => ctxValue.includes(x));
      } else {
        return ctxValue.includes(value);
      }
    } else {
      if (Array.isArray(value)) {
        return value.includes(ctxValue);
      } else {
        return value === ctxValue;
      }
    }
  });
}

/**
 * Determines visibility based on whitelist and blacklist criteria.
 *
 * @template ContextType - The type of the context object
 * @param params - Configuration object
 * @param params.context - The context object to evaluate
 * @param params.whitelist - Optional array of criteria. If any match, item is visible
 * @param params.blacklist - Optional array of criteria. If any match, item is hidden
 * @returns Tuple of [isVisible, matchedCriteria]
 *
 * Evaluation order:
 * 1. If context matches any blacklist criteria: returns [false, matched criteria]
 * 2. If no whitelist is defined: returns [true, undefined]
 * 3. If context matches any whitelist criteria: returns [true, matched criteria]
 * 4. Otherwise: returns [false, undefined]
 */
export function resolveVisibility<ContextType extends object>({
  context,
  whitelist,
  blacklist,
}: {
  context: ContextType;
  whitelist?: ConditionalWrapperCriteria<ContextType>[];
  blacklist?: ConditionalWrapperCriteria<ContextType>[];
}): [boolean, ConditionalWrapperCriteria<ContextType> | undefined] {
  // If context matches with any blacklist criteria: not visible
  if (blacklist) {
    const blCriteria = blacklist.findIndex((criteria) =>
      matchesCriteria(context, criteria),
    );

    if (blCriteria !== -1) {
      return [false, blacklist[blCriteria]];
    }
  }

  // Empty/undefined whitelist means: visible
  if (!whitelist) return [true, undefined];

  // If context matches any whitelist criteria: visible. Otherwise: not visible
  const wlCriteria = whitelist.findIndex((criteria) =>
    matchesCriteria(context, criteria),
  );
  if (wlCriteria !== -1) {
    return [true, whitelist[wlCriteria]];
  }

  return [false, undefined];
}
